import { HttpResource, IHeader } from '../resources/http.resource';
import { Url, IUrlObject } from '../resources/url.resource';
import {ActiveAuth, activeAuthIsNull} from "../auth/activeAuth";
import EventSecretRefreshToken from "./EventSecretRefreshToken";
import EventPasswordRefreshToken from "./EventPasswordRefreshToken";
import * as error from "../error";

export interface HttpDataOptions extends HttpOptions {
    json?: Object;
}

export interface HttpOptions {
    pathname: Array<string>;
    headers?: Array<IHeader>;
    qs?: Object;
}

export interface QueryGraphQLOptions {
    variables: Record<string, any>;
}

export class ServiceApi {

    private serviceUrlObj: IUrlObject;
    private activeAuth;
    private personKey?: string;

    constructor(serviceUrlObj: IUrlObject, activeAuth?: Object) {
        this.serviceUrlObj = serviceUrlObj;
        this.activeAuth = (activeAuth) ?activeAuth : null;
    }

    public getActiveAuth() {
        return this.activeAuth;
    }

    public setPersonKey(personKey: string) {
        this.personKey = personKey;
    }

    private getBaseUrlFromObj(obj: IUrlObject) {
        return `${obj.protocol}://${obj.hostname}${(obj.port) ? ':' + obj.port : ''}/`;
    }

    /**
     * Get a new Http Resource.
     * The resource is automatically authorized if activeAuth is present.
     * @returns {Promise<HttpResource>}
     */
    protected newHttpResource(): Promise<HttpResource> {
        return new Promise((resolve, reject) => {

            const http = new HttpResource();
            http.withCredentials = true;
            http.setBaseUrl(this.getBaseUrlFromObj(this.serviceUrlObj));

            if (this.personKey) http.addHeader({name: 'x-joy-personid', data: this.personKey});

            if (activeAuthIsNull(this.activeAuth)) {
                resolve(http);
                return;
            }
            this.activeAuth.getAccessToken((err, accessTokenObj) => {
                if (err) {
                    reject(err);
                    return;
                }
                http.addHeader({name: 'Authorization', data: `Bearer ${accessTokenObj.token()}`});
                resolve(http);
            });
        })
    }

    private parseOptions(options: HttpDataOptions) {
        return {
            url: (options.qs) ? Url.join(...options.pathname) + Url.param(options.qs) : Url.join(...options.pathname),
            headers: (options.headers) ? options.headers : [],
            data: (options.json) ? options.json : null
        }
    }

    // Generic Auth'd Methods
    public getRequest(options: HttpOptions) {
        return this.newHttpResource().then(http => {
            let { url, headers, data } = this.parseOptions(options);
            return http.get(url, headers);
        });
    }

    public postRequest(options: HttpDataOptions) {
        return this.newHttpResource().then(http => {
            let { url, headers, data } = this.parseOptions(options);
            return http.post(url, headers, data);
        });
    }

    public putRequest(options: HttpDataOptions) {
        return this.newHttpResource().then(http => {
            let { url, headers, data } = this.parseOptions(options);
            return http.put(url, headers, data);
        });
    }

    public deleteRequest(options: HttpOptions) {
        return this.newHttpResource().then(http => {
            let { url, headers, data } = this.parseOptions(options);
            return http.delete(url, headers);
        });
    }

    // Event Methods
    public createEvent(options: any) {
        return this.postRequest({
            pathname: ['events'],
            qs: options
        })
    }

    public quickStartCreateEvent(options: any) {
        return this.postRequest({
            pathname: ['quickcreate'],
            qs: {type: 'wedding'},
            json: {data: options}
        });
    }

    public joinEventWithSecret(code: string) {
        return this.postRequest({
            pathname: ["events"],
            qs: {type: "join"},
            json: {
                join: {
                    type: "eventSecret",
                    eventSecret: code.trim()
                }
            }
        });
    }

    public getEventHelpContact(eventId: string) {
        return this.getRequest({
            pathname: ["events", eventId, "helpContact"],
        });
    }

    public getPublicEventInfo(eventId: string) {
        return this.getRequest({
            pathname: ['events', eventId, 'publicInfo'],
        })
    }

    public getEvent(eventId: string) {
        return this.getRequest({
            pathname: ["events", eventId]
        })
    }

    public getEventV2(eventId) {
        return this.getRequest({
            pathname: ["events", eventId],
            headers: [{name: "accept-version", data: "0.0.2"}]
        });
    }

    public getEventsV2() {
        return this.getRequest({
            pathname: ["events"],
            headers: [{name: "accept-version", data: "0.0.2"}]
        });
    }

    public getEventIdFromName(eventName: string) {
        return this.getRequest({
            pathname: ["eventname"],
            qs: {
                eventName: eventName
            }
        })
    }

    public getServiceApiForEventSecret(eventSecret) {
        var rt = new EventSecretRefreshToken(this, eventSecret);
        var activeAuth = new ActiveAuth(rt);
        var api = new ServiceApi(this.serviceUrlObj, activeAuth);
        return api;
    }

    public getServiceApiForEventPassword = function (eventId, eventPassword) {
        var rt = new EventPasswordRefreshToken(this, eventId, eventPassword);
        var activeAuth = new ActiveAuth(rt);
        var api = new ServiceApi(this.serviceUrlObj, activeAuth);
        return api;
    };

    public createEventPermitWithEventSecret(eventSecret: string) {
        return this.postRequest({
            pathname: ["permits"],
            qs: {
                type: "events"
            },
            json: {
                type: "eventSecret",
                eventSecret: eventSecret,
                level: "member"
            }
        });
    }

    public createEventPermitWithEventPassword = function (eventId: string, eventPassword: string) {
        return this.postRequest({
            pathname: ["permits"],
            qs: {
                type: "events"
            },
            json: {
                type: "eventPassword",
                eventPassword: {eventId, eventPassword},
                level: "member"
            }
        });
    };

    public getEventAssetStores(eventId, options) {
        return this.getRequest({
            pathname: ["events", eventId, "assetStores"],
            qs: options
        });
    };

    public getEventUserProfiles(eventId, fieldsArray) {
        return this.getRequest({
            pathname: ["events", eventId, "users"],
            qs: {
                fields: fieldsArray.join(",")
            }
        });
    }

    public getEventJoinSecret(eventId: string) {
        return this.getRequest({
            pathname: ['events', eventId, 'joinSecret']
        })
    }

    public requestEventJoinSecret(eventId: string, guestInfo) {
        return this.postRequest({
            pathname: ['events', eventId, 'requestCode'],
            json: {
                guestInfo
            }
        })
    }

    public requestAccessEmail(eventId: string, guestInfo) {
        return this.postRequest({
            pathname: ['events', eventId, 'requestAccessEmail'],
            json: {
                guestInfo
            }
        })
    }

    public graphQLQuery(query: string, options?: QueryGraphQLOptions) {
        const json = (options
            ? { ...options, query }
            : { query }
        );
        return this.postRequest({
            pathname: ['graphql'],
            json,
        })
    }

    public queryGraphQLWithErrors(query: string, options?: QueryGraphQLOptions) {
        return this.graphQLQuery(query, options)
        .then(({errors, data}) => {
            if(errors && errors.length > 0){
                let err = new error.client.Unauthorized(errors[0].message);
                err.statusCode = errors[0].statusCode;
                throw (err);
            }
            return data;
        });
    };


}
